import { useLayoutEffect, useRef } from 'react';
import type { GalleryImage } from '../content/types';
import { SmartImg } from '../ui/SmartImg';

const W = 1080;
const ANIM_MS = 260;

export interface SwipeApi {
  go: (d: 1 | -1) => void;
}

/**
 * Swipe propio con Pointer Events (táctil o mouse, según reporte el driver del IR).
 * Solo 3 diapositivas montadas (anterior, actual, siguiente) y el arrastre mueve UNA capa con
 * transform directo sobre el DOM: sin renders de React por cada pointermove.
 */
export function SwipeTrack({
  items,
  index,
  onIndex,
  onTap,
  apiRef,
}: {
  items: GalleryImage[];
  index: number;
  onIndex: (i: number) => void;
  onTap: () => void;
  apiRef?: React.MutableRefObject<SwipeApi | null>;
}) {
  const n = items.length;
  const track = useRef<HTMLDivElement>(null);
  const busy = useRef(false);
  const drag = useRef<{ id: number; x0: number; t0: number; dx: number; k: number; moved: boolean } | null>(null);

  const setX = (x: number, animate: boolean) => {
    const el = track.current;
    if (!el) return;
    el.style.transition = animate ? `transform ${ANIM_MS}ms cubic-bezier(0.2, 0.7, 0.2, 1)` : 'none';
    el.style.transform = `translate3d(${x}px,0,0)`;
  };

  const commit = (d: 1 | -1) => {
    if (busy.current || n < 2) return;
    busy.current = true;
    setX(-d * W, true);
    window.setTimeout(() => {
      onIndex((index + d + n) % n);
      busy.current = false;
    }, ANIM_MS);
  };

  if (apiRef) apiRef.current = { go: commit };

  // Tras cambiar de índice, la nueva actual ya está en el centro: se resetea la pista antes de pintar
  useLayoutEffect(() => {
    setX(0, false);
  }, [index, items]);

  const onDown = (e: React.PointerEvent) => {
    if (busy.current || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const k = (track.current?.parentElement?.getBoundingClientRect().width || W) / W;
    drag.current = { id: e.pointerId, x0: e.clientX, t0: performance.now(), dx: 0, k, moved: false };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    d.dx = (e.clientX - d.x0) / d.k;
    if (Math.abs(d.dx) > 10) d.moved = true;
    if (d.moved && n > 1) setX(d.dx, false);
  };
  const onUp = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (!d.moved) {
      setX(0, false);
      onTap();
      return;
    }
    const v = d.dx / Math.max(1, performance.now() - d.t0); // px/ms
    if (n > 1 && (Math.abs(d.dx) > 160 || Math.abs(v) > 0.45)) commit(d.dx < 0 ? 1 : -1);
    else setX(0, true);
  };

  const offsets = n > 1 ? [-1, 0, 1] : [0];
  return (
    <div
      className="fill overflow-hidden"
      style={{ touchAction: 'none' }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <div ref={track} className="fill" style={{ willChange: 'transform' }}>
        {offsets.map((o) => {
          const it = items[(index + o + n) % n];
          if (!it) return null;
          return (
            <div key={n > 2 ? it.id : `${it.id}@${o}`} className="absolute top-0 h-full" style={{ left: o * W, width: W }}>
              <SmartImg file={it.main} lqip={it.lqip} fit="contain" className="fill" style={{ backgroundColor: '#1C294C' }} alt={it.caption || ''} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
